import { AlertService } from '@shared/services/alert.service';
import { Subscription } from 'rxjs';
import { Component, EventEmitter, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { IMultiPrintDevice } from '@multiprint/abstract/multi-print-device';
import { IMultiPrintFileMaterial } from '@shared/modules/print-file-shared/interfaces/print-upload-preview-material';
import { statusTypes } from '@shared/constants/status-types';
import { MaterialsComparer } from '@shared/helpers/materials-comparer';
import { ApiService } from '@shared/services/api.service';
import { DevicePublisherService } from '@shared/services/publishers/device-publisher.service';
import { NbDialogRef } from '@nebular/theme';
import { MultiprintComponent } from '../multiprint/multiprint.component';
import { champerTemperatureStatuses } from '@shared/constants/champerTemperatureStatuses';

@Component({
  selector: 'app-multiprint-third-step',
  templateUrl: './multiprint-third-step.component.html',
  styleUrls: ['./multiprint-third-step.component.scss']
})
export class MultiprintThirdStepComponent implements OnInit, OnDestroy {
  @Output() finishStep = new EventEmitter<void>();
  @Input() onlyDeviceIds: string[] = [];
  @Input() uploadFileId!: string;

  private _deviceType: number = 0;
  
  @Input() set deviceType(value: number) {
    if (value) {
      this._deviceType = value;
      this.getMultiPrintDevicesByType(this._deviceType);
    }
  }

  get deviceType(): number {
    return this._deviceType;
  }

  private _fileMaterials: IMultiPrintFileMaterial[] = [];
  @Input() set fileMaterials(value: IMultiPrintFileMaterial[]) {
    if (value) {
      this._fileMaterials = value;
      this.printIsPossible = this.isPrintPossible();
    }
  }

  get fileMaterials(): IMultiPrintFileMaterial[] {
    return this._fileMaterials;
  }

  subscribtions: Subscription = new Subscription();
  statusTypes = statusTypes;
  selectedMaterialId: number = -9;
  showDevicesWithStatus: number = statusTypes.idle;
  devices: IMultiPrintDevice[] = [];
  loading: boolean = true;
  printIsPossible: boolean = false;
  forcePrinting: boolean = false;
  differenceInMaterials: boolean = false;
  emptyMaterial: boolean = false;
  isPrintingState: boolean = false;
  disableStopButton: boolean = true;
  chamberTemperatureNotStabilized = false;

  constructor(private api: ApiService, private devicePublisherService: DevicePublisherService,
    private alertService: AlertService, protected dialogRef: NbDialogRef<MultiprintComponent>) { }

  ngOnDestroy(): void {
    this.subscribtions?.unsubscribe();
  }

  ngOnInit(): void {
    this.refreshDeviceStatuses();
  }

  refreshDeviceStatuses() {
    this.subscribtions.add(
      this.devicePublisherService.getDeviceStatusObservable().subscribe(res => {
        var index = this.devices.findIndex(d => d.id == res.deviceId);
        if (index !== -1) {
          this.devices[index].statusCode = res.deviceStatus.statusCode;
          this.devices[index].reason = res.deviceStatus.reason;
          this.devices[index].materials = res.deviceStatus.currentLoadedMaterials;
        }

        if (!this.disableStopButton) {
          this.disableStopButton = !this.canStopPrinting();
        }
      })
    );
  }

  getMultiPrintDevicesByType(type: number) {
    this.loading = true;
    this.subscribtions.add(
      this.api.getMultiPrintDevicesByType(type)
        .subscribe(res => {
          this.devices = res;
          this.loading = false;
        })
    );
  }

  trackByFn(index: number, item: any) {
    return item.id;
  }

  toggleSelectDevice(device: IMultiPrintDevice) {
    var index = this.devices.findIndex(d => d.id == device.id);

    if (index === -1) {
      return;
    }

    this.devices[index].isSelected = !this.devices[index].isSelected;
    this.forcePrinting = false;
    this.printIsPossible = this.isPrintPossible();
    this.emptyMaterial = this.isEmptyMaterial();
    this.differenceInMaterials = this.isDifferenceInMaterials();
    this.chamberTemperatureNotStabilized = this.isChamberTemperatureNotStabilized();
  }

  protected isEmptyMaterial(): boolean {
    return !!this.devices.find(device => device.isSelected && !device.materials?.length);
  }

  isChamberTemperatureNotStabilized(){
    var selectedDevices = this.devices.filter(p => p.isSelected);
    for (let index = 0; index < selectedDevices.length; index++) {
      const champerTemperatureStatus = selectedDevices[index].champerTemperatureStatus;

      if (champerTemperatureStatus !== champerTemperatureStatuses.STABILIZED) {
        return true;
      }
    }

    return false;
  }

  isDifferenceInMaterials() {
    var selectedDevices = this.devices.filter(p => p.isSelected);

    for (let index = 0; index < selectedDevices.length; index++) {
      const loadedMaterials = selectedDevices[index].materials.map(({ id }) => id);

      if (!MaterialsComparer.equals(loadedMaterials, this.fileMaterials.map(p => p.id))) {
        return true;
      }
    }

    return false;
  }

  isPrintPossible() {
    return (this.devices.some(p => p.isSelected) && !this.isDifferenceInMaterials() && !this.isEmptyMaterial() && !this.isChamberTemperatureNotStabilized()) || this.forcePrinting
  }

  confirmPrint(force: boolean) {
    this.forcePrinting = force;
    this.printIsPossible = this.isPrintPossible();
  }

  startMultiPrint() {
    var selectedDeviceIds = this.devices.filter(p => p.isSelected).map(p => p.id);
    
    if (selectedDeviceIds.length === 0 || !this.uploadFileId) {
      return;
    }

    this.unselectAllDevices();
    this.printIsPossible = false;
    this.differenceInMaterials = false;
    this.chamberTemperatureNotStabilized = false;

    this.subscribtions.add(
      this.api.postStartMultiPrint(this.uploadFileId, selectedDeviceIds)
        .subscribe({
          next: res => {
            this.isPrintingState = true;
            this.finishStep.next();

            res.map(r => {
              var index = this.devices.findIndex(d => d.id == r.deviceId);
              
              if (index !== -1) {
                this.devices[index].uploadId = r.uploadId;
              }
            })
          }
        })
    );
  }

  stopMultiPrint() {
    var deviceIds = this.devices.filter(d => d.uploadId).map(d => d.id);
    if (deviceIds.length === 0) {
      return;
    }

    this.subscribtions.add(
      this.api.postStopMultiPrint(deviceIds)
        .subscribe(() => {
          this.alertService.success("bulk.stop-success", 4000);
          this.dialogRef.close();
        })
    );
  }

  showMatchingDevices() {
    this.onlyDeviceIds = [];
    this.showDevicesWithStatus = statusTypes.idle;
  }

  showAllMatchingDevices() {
    this.onlyDeviceIds = [];
    this.showDevicesWithStatus = statusTypes.all;
    this.selectedMaterialId = -9;
  }

  unselectAllDevices() {
    this.devices.forEach(d => d.isSelected = false);
  }

  canStopPrinting() {
    return this.devices.some(p => p.statusCode === statusTypes.printing && p.uploadId);
  }
}
